/** @jsxImportSource @emotion/react */

import { msg } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import classNames from 'classnames';
import { ChevronLeft, ChevronRight, CloudCog, Joystick as JoystickIcon, RefreshCw } from 'lucide-react';
import { resetGameInfo, syncGameInfo } from 'modules/overlay-v2/store/game-info/actions';
import { useDispatchWithToast } from 'store/use-dispatch-with-toast';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useIsMobileImproved from 'shared/hooks/use-is-mobile-improved';
import EmptyButton from 'ud-ui/components/empty-button';
import UDText from 'ud-ui/components/text';
import supabase from 'modules/core/supabase/supabase';
import { setHideControlPanel } from 'modules/overlay-v2/store/overlay/overlay';
import { selectBroadCastState, selectIsHideControlPanel } from 'modules/overlay-v2/store/overlay/selectors';
import { resetTime, syncTime } from 'modules/overlay-v2/store/time/actions';
import useConnectionIndicator from 'modules/overlay-v2/ui/hooks/use-connection-indicator';
import useGameInfo from 'modules/overlay-v2/ui/hooks/use-game-info';
import { useAppSelector } from 'store/hooks';
import Indicator from '../components/indicator';
import Text from '../components/text';
import { Dashboard } from './dashboard';
import { Fouls } from './fouls';
import { NetworkChaos } from './network-chaos';
import { Period } from './period';
import { Score } from './score';
import { Button, HideButton, MainBlock, OpenPanelWrapper } from './style';
import Team from './team';
import { TeamsName } from './teams-name';
import { theme } from 'styles/theme/theme';
import { VideoMarkersPanel } from 'modules/video-markers/ui/components';
import { VideoMarkersState } from 'modules/video-markers/ui/hooks';

interface JoystickProps {
  /** Состояние видеометок оверлея; без настройки в ссылке панель не показывается. */
  videoMarkers?: VideoMarkersState;
}

function Joystick({ videoMarkers }: JoystickProps) {
  // `matchId` — из стора трансляции: в роутах оверлея такого сегмента нет, и `useParams()`
  // давал `NaN` (IMGM-1435).
  const { t } = useLingui();
  const { matchId } = useAppSelector(selectBroadCastState);
  const isMobileImproved = useIsMobileImproved();

  const dispatch = useDispatch();
  const dispatchWithToast = useDispatchWithToast();
  const { match, isSynchronization } = useGameInfo();
  const { isJoystickActive } = useConnectionIndicator();
  const isHideControlPanel = useSelector(selectIsHideControlPanel);

  const [devDisableConnection, setDevDisableConnection] = useState(false);

  const isDev = process.env.REACT_APP_ENVIRONMENT !== 'production';

  useEffect(() => {
    if (isMobileImproved) {
      dispatch(setHideControlPanel(true));
    }
  }, [dispatch, isMobileImproved]);

  const onClickReset = useCallback(() => {
    dispatch(resetGameInfo());
    dispatch(resetTime());
  }, [dispatch]);

  const handleClickSync = useCallback(() => {
    if (isSynchronization) return;
    if (!match) return;
    const matchId = match.id;
    // Раньше провал молчал (кроме мёртвой ветки «не заданы таймы», удалённой в IMGM-1549).
    dispatchWithToast(syncGameInfo({ matchId }), { error: msg`Не удалось синхронизировать данные матча` });
    dispatch(syncTime({ matchId }));
  }, [dispatch, dispatchWithToast, isSynchronization, match]);

  const toggleHideControlPanel = useCallback(() => {
    dispatch(setHideControlPanel(!isHideControlPanel));
  }, [dispatch, isHideControlPanel]);

  const handleDisconnect = useCallback(() => {
    if (matchId === undefined) return;

    setDevDisableConnection((prevState) => {
      if (prevState) {
        supabase.matchChronology.observeChanges(matchId);
      } else {
        supabase.matchChronology.stopObserveChanges().then((result) => {
          console.debug({ disableConnection: result });
          // Статус пишется в общее поле, а не в локальный стейт: по нему живут и индикатор,
          // и резервный опрос — иначе разрыв «для вида» не проверить.
          if (result === 'ok') {
            supabase.matchChronology.connectionStatus.value = 'CLOSED';
          }
        });
      }

      return !prevState;
    });
  }, [matchId]);

  return isHideControlPanel ? (
    <>
      <div style={{ position: 'relative' }} />
      <OpenPanelWrapper opacity={0} position={'right'}>
        <HideButton color={'white'} onClick={toggleHideControlPanel} icon={<ChevronLeft style={{ zIndex: 1 }} />} />
      </OpenPanelWrapper>
    </>
  ) : (
    <>
      <MainBlock className={'flex flex-col'} style={{ zIndex: theme.zIndex.sticky }}>
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
          <EmptyButton
            style={{ backgroundColor: theme.colors.AccentNaplesYellow, height: '100%', width: 34, minWidth: 34 }}
            color={'white'}
            onClick={toggleHideControlPanel}
            icon={<ChevronRight style={{ zIndex: 1 }} />}
          />
          <Indicator
            className={'w-full'}
            text={isJoystickActive ? t`Соединение с джойстиком установленно!` : t`Нет соединения с джойстиком!`}
            icon={<JoystickIcon size={20} />}
            isActive={isJoystickActive}
          />
        </div>
        {isDev && (
          <Button color={devDisableConnection ? 'darkgreen' : 'darkred'} onClick={handleDisconnect} className={'flex-center'}>
            <Text size={'medium'} color={'inherit'}>
              {/* eslint-disable-next-line lingui/no-unlocalized-strings -- отладочная кнопка, только вне production */}
              {devDisableConnection ? 'Включить соединение' : 'Разорвать соединение'}
            </Text>
            <UDText type={'body-text'} style={{ color: 'inherit' }}>
              {devDisableConnection ? 'V' : 'X'}
            </UDText>
          </Button>
        )}
        {videoMarkers && <VideoMarkersPanel state={videoMarkers} />}
        {isDev && <NetworkChaos />}
        <TeamsName />
        <Score />
        <Fouls />
        <Period />
        <Dashboard />
        <Button
          color={theme.colors.AccentNaplesYellow}
          onClick={handleClickSync}
          className={classNames('flex-center', { loading: isSynchronization })}
        >
          {!isSynchronization && (
            <>
              <Text size={'medium'} color={'inherit'}>
                <Trans>Синхронизировать</Trans>
              </Text>
              <CloudCog size={14} />
            </>
          )}
        </Button>
        <Button color={theme.colors.Gray} onClick={onClickReset} className={'flex-center'}>
          <Text size={'medium'} color={'inherit'}>
            <Trans>Сбросить все настройки</Trans>
          </Text>
          <RefreshCw size={14} />
        </Button>
        <Team teamIndex={0} />
        <Team teamIndex={1} />
      </MainBlock>
    </>
  );
}

export default Joystick;
